import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  STAGGERED_TOP_THREE,
  STANDARD_TOP_THREE
} from '../components/top-three/graphql/top-three-queries';

import {
  useFitment,
  useModal,
  useMyAccount,
  useMyStore,
  useProductQuantity,
  useQuery,
  useTreadwellPersonas,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import {
  getVehicleInput,
  parseTireSize
} from '../../../shared/utils/fitment/fitment';
import { getTreadwellGQLInput } from '../../../shared/utils/treadwell/treadwell';

import { FuelTypeEnum } from '../../../shared/types/graphql-types';
import { MODAL_ROUTE_PATTERN } from '../../../shared/constants/modals';
import { PRODUCT_TYPES } from '../../../shared/constants/products';
import {
  PRODUCT_TILE_TYPES,
  TREADWELL_DEFAULT_PERSONA_NAME,
  treadwellDefaults
} from '../../../shared/constants/treadwell';

export const TOP_3_TYPES = {
  HIGHEST_RATED: 'HR',
  LOWEST_PRICE: 'LPA',
  LOWEST_PRICE_WEB: 'LPW',
  ORIGINAL_EQUIPMENT: 'OE',
  RECOMMENDED: 'REC'
};

export const TOP_3_POSITIONS = ['101', '102', '103'];

export default function usePlpTopThree({
  isBrandSearch,
  productType,
  staggeredSearchType,
  tireSizes
}) {
  const [lowestPriceProduct, setLowestPriceProduct] = useState<
    {} | undefined
  >();
  const { selectedFitment } = useFitment();
  const { hash } = useLocation();
  const { isOpen } = useModal();
  const [queryVariables, setQueryVariables] = useState<{} | undefined>();

  const { myStore } = useMyStore();
  const { defaultQuantity: quantity } = useProductQuantity();

  const { treadwellPreferences } = useTreadwellPreferences();
  const { getTreadwellPersonaData } = useTreadwellPersonas();

  const { isFleetAccount } = useMyAccount();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const storeCode = myStore?.code;

  const vehicleInfo = useMemo(() => {
    if (selectedFitment) {
      return getVehicleInput(selectedFitment);
    }
  }, [selectedFitment]);

  useEffect(() => {
    async function setupQueryVariables() {
      if (isOpen || MODAL_ROUTE_PATTERN.test(hash)) {
        return;
      }

      if (!storeCode) {
        setQueryVariables(undefined);
        return;
      }

      let treadwellRequest = getTreadwellGQLInput(
        selectedFitment,
        treadwellPreferences,
        isFleetTreadwell
      );

      if (!treadwellRequest) {
        const treadwellDefaultsToUse = {
          ...treadwellDefaults
        };

        try {
          const { personasList } = await getTreadwellPersonaData(
            selectedFitment
          );
          const defaultPersona = personasList?.find(
            persona => persona.name === TREADWELL_DEFAULT_PERSONA_NAME
          );

          if (defaultPersona) {
            treadwellDefaultsToUse.drivingStyle =
              TREADWELL_DEFAULT_PERSONA_NAME;
            defaultPersona.tooltips?.forEach((tooltip, i) => {
              treadwellDefaultsToUse[`priority${i + 1}`] = tooltip?.id;
            });
          }
        } catch (e) {
          console.error(
            'Unable to fetch persona defaults for Suburb & City',
            e
          );
        }

        if (selectedFitment) {
          treadwellDefaultsToUse.fuelType =
            selectedFitment?.fuelType || FuelTypeEnum.Ice;
        }

        treadwellRequest = {
          ...treadwellDefaultsToUse,
          isOESize: selectedFitment?.isOE || false,
          quantity,
          zipCode: myStore?.address?.postalCode
        };
      }

      const frontSize = parseTireSize(selectedFitment?.frontTireSize);
      const searchInput: {
        front: { aspectRatio: string; diameter: string; width: string } | null;
        rear?: { aspectRatio: string; diameter: string; width: string } | null;
        staggeredType?: string;
        treadwell: {
          fuelType: string;
          isOESize: boolean;
          isWinterSearch: boolean;
          milesPerYear: number;
          quantity: number;
          zipCode: string;
        };
      } = {
        front: frontSize ||
          tireSizes?.front || { aspectRatio: '', diameter: '', width: '' },
        treadwell: treadwellRequest
      };

      if (staggeredSearchType) {
        searchInput.rear =
          parseTireSize(selectedFitment?.rearTireSize) || tireSizes?.rear;
        searchInput.staggeredType = staggeredSearchType;
      }

      setQueryVariables({
        searchInput,
        storeCode,
        vehicleInfo
      });
    }
    setupQueryVariables();
  }, [
    getTreadwellPersonaData,
    hash,
    isFleetTreadwell,
    storeCode,
    selectedFitment,
    staggeredSearchType,
    treadwellPreferences,
    quantity,
    myStore,
    tireSizes,
    vehicleInfo,
    isOpen
  ]);

  const { data, hasBeenCalled, isLoading } = useQuery(
    staggeredSearchType ? STAGGERED_TOP_THREE : STANDARD_TOP_THREE,
    {
      skip: !queryVariables,
      variables: queryVariables
    }
  );

  const hasFetchedData = hasBeenCalled && !isLoading;
  const tiles = staggeredSearchType
    ? data?.productSearch?.staggeredTiles?.productTiles
    : data?.productSearch?.standardTiles?.productTiles;
  const shouldDisplay = Array.isArray(tiles) && tiles.length === 3;

  useEffect(() => {
    if (
      !isBrandSearch &&
      productType === PRODUCT_TYPES.TIRES &&
      hasFetchedData &&
      tiles?.length > 0
    ) {
      const lowestPriceTile = tiles.filter(
        tile =>
          tile.tile === PRODUCT_TILE_TYPES.LOWEST_PRICE ||
          tile.tile === PRODUCT_TILE_TYPES.LOWEST_PRICE_WEB
      );

      if (lowestPriceTile?.length > 0) {
        const tileData = lowestPriceTile[0];
        const isOnlineOnly =
          tileData.tile === PRODUCT_TILE_TYPES.LOWEST_PRICE_WEB;
        const frontPrice = tileData?.productData?.price?.value || 0;
        const rearPrice = tileData?.productData?.rearProduct?.price?.value || 0;
        const price = frontPrice + rearPrice;

        setLowestPriceProduct({
          isOnlineOnly,
          price: price > 0 ? price : undefined
        });
      }
    }
  }, [
    hasFetchedData,
    isBrandSearch,
    productType,
    setLowestPriceProduct,
    tiles
  ]);

  return {
    hasFetchedData,
    lowestPriceProduct,
    shouldDisplay,
    tiles
  };
}
